import React, { useState, useEffect, useRef } from 'react';
import { checkAvailability } from '../services/courseService';
import { AlertCircle, Users, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

/**
 * Enhanced availability indicator that shows gender-specific availability
 * Uses the new optimized API response with genders and messages
 */
const GenderAvailabilityIndicator = ({ 
  courseId, 
  onAvailabilityChange,
  showDetails = true,
  compact = false,
  className = '',
  autoRefresh = false, // Disabled by default - user must click refresh
  refreshInterval = 30000, // 30 seconds if auto-refresh is enabled
  isConfirmedFull = false // Override when we know course is actually full
}) => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const cacheRef = useRef(new Map()); // Cache availability by courseId
  const intervalRef = useRef(null);

  const loadAvailability = async (forceRefresh = false) => {
    try {
      // Check cache first (unless forced refresh)
      if (!forceRefresh && cacheRef.current.has(courseId)) {
        const cached = cacheRef.current.get(courseId);
        const cacheAge = Date.now() - cached.timestamp;
        
        // Use cache if less than 10 seconds old
        if (cacheAge < 10000) {
          setAvailability(cached.data);
          setLastUpdated(cached.timestamp);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);
      
      const availabilityData = await checkAvailability(courseId);
      //console.log('Gender availability data:', availabilityData);
      
      // Update cache
      const now = Date.now();
      cacheRef.current.set(courseId, {
        data: availabilityData,
        timestamp: now
      });
      
      setAvailability(availabilityData);
      setLastUpdated(now);
      setLoading(false);
      
      // Notify parent component
      if (onAvailabilityChange) {
        onAvailabilityChange(availabilityData);
      }
    } catch (err) {
      console.error('Error loading availability:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    // Don't refresh if course is confirmed full
    if (isConfirmedFull) {
      return;
    }
    setIsRefreshing(true);
    await loadAvailability(true); // Force refresh, bypass cache
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (!courseId) return;

    // IMPORTANT: Skip loading if course is confirmed full
    // This prevents overwriting the "full" status set by CourseStatusIndicator
    if (isConfirmedFull) {
      setAvailability({
        availableSpots: 0,
        totalSpots: 0,
        isAvailable: false,
        genders: {},
        messages: ['Course is full']
      });
      setLoading(false);
      return;
    }

    // Load initial availability
    loadAvailability();

    // Only set interval if autoRefresh is enabled
    if (autoRefresh) {
      intervalRef.current = setInterval(() => loadAvailability(true), refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [courseId, autoRefresh, refreshInterval, isConfirmedFull]);



  if (error) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <AlertCircle className="w-4 h-4 text-red-500" />
        <span className="text-sm text-red-600">Unable to check availability</span>
      </div>
    );
  }

  if (!availability) return null;

  // Use override if course is confirmed full
  const { genders, messages, isAvailable } = availability;
  const availableSpots = isConfirmedFull ? 0 : availability.availableSpots;

  // Get overall status color
  const getOverallStatusColor = () => {
    if (availableSpots > 3) return 'text-green-600 bg-green-50';
    if (availableSpots > 0) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  // Get gender-specific status icon
  const getStatusIcon = (reason) => {
    switch(reason) {
      case 'ok':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'held':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'full':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  // Get gender-specific color
  const getGenderColor = (gender) => {
    if (!genders?.[gender]) return 'bg-gray-100 text-gray-600';
    
    const { reason, available } = genders[gender];
    if (reason === 'ok' && available > 0) return 'bg-green-100 text-green-700';
    if (reason === 'held') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  // Compact view - just show overall status
  if (compact) {
    return (
      <div className={`inline-flex items-center space-x-2 ${className}`}>
        <div className={`inline-flex items-center px-2 py-1 rounded-md ${getOverallStatusColor()}`}>
          <Users className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">
            {availableSpots > 0 ? `Some spaces` : 'Full'}
          </span>
        </div>
        <button
          onClick={handleManualRefresh}
          disabled={isRefreshing || loading}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh availability"
        >
          <RefreshCw className={`w-4 h-4 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    );
  }

  // Full view with gender breakdown
  return (
    <div className={`space-y-2 ${className}`}>
      {/* Overall availability message with refresh button */}
      <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${getOverallStatusColor()}`}>
        <Users className="w-5 h-5" />
        <div className="flex-1">
          <p className="text-sm font-semibold">
            {isAvailable ? `Spots available` : 'Course is full'}
          </p>
          {lastUpdated && (
            <p className="text-xs mt-0.5 opacity-75">
              Updated {new Date(lastUpdated).toLocaleTimeString()}
            </p>
          )}
{/*           {messages && messages.length > 0 && (
            <p className="text-xs mt-0.5 opacity-90">
              {messages[0]}
            </p>
          )} */}
        </div>
        <button
          onClick={handleManualRefresh}
          disabled={isRefreshing || loading}
          className="p-1.5 rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Refresh availability"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Gender-specific breakdown */}
{/*       {showDetails && genders && (
        <div className="grid grid-cols-3 gap-2">
          {['male', 'female', 'other'].map(gender => {
            const genderData = genders[gender];
            if (!genderData) return null;

            return (
              <div 
                key={gender}
                className={`flex flex-col items-center p-2 rounded-lg ${getGenderColor(gender)}`}
              >
                <div className="flex items-center space-x-1">
                  {getStatusIcon(genderData.reason)}
                  <span className="text-xs font-medium capitalize">
                    {gender}
                  </span>
                </div>
                <span className="text-lg font-bold">
                  {genderData.available}
                </span>
                <span className="text-xs opacity-75">
                  of {genderData.total}
                </span>
                {genderData.reserved > 0 && (
                  <span className="text-xs mt-1 flex items-center">
                    <Clock className="w-3 h-3 mr-0.5" />
                    {genderData.reserved} held
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )} */}

      {/* Detailed messages */}
{/*       {showDetails && messages && messages.length > 0 && (
        <div className="space-y-1 px-3 py-2 bg-gray-50 rounded-lg">
          {messages.map((message, index) => (
            <div key={index} className="flex items-start space-x-2">
              <span className="text-gray-400 text-xs">•</span>
              <span className="text-xs text-gray-700">{message}</span>
            </div>
          ))}
        </div>
      )} */}
    </div>
  );
};

export default GenderAvailabilityIndicator;